import { combineReducers } from 'redux';
import storage from 'redux-persist/lib/storage';
import { createTransform } from 'redux-persist';
import { PRACTICE_SWITCH_RESET, PRACTICE_AGNOSTIC_SLICES, projectPreservedState } from './practiceSwitchReset';
// slices
import mailReducer from './slices/mail';
import practiceReducer from './slices/practice';
import singlePracticeReducer from './slices/singlePractice';
import practiceTreatmentReducer from './slices/practiceTreatment';
import practiceMessageReducer from './slices/practiceMessage';
import practiceTreatmentListReducer from './slices/practiceTreatmentList';
import practiceUserReducer from './slices/practiceUser';
import calendarReducer from './slices/calendar';
import kanbanReducer from './slices/kanban';
import userReducer from './slices/user';
import leadReducer from './slices/lead';
import attendanceReview from './slices/attendanceReview.ts';
import forgotpasswordReducer from './slices/forgotpass';
import tagsCreateReducer from './slices/tagsCreate';
import practiceIntegrationReducer from './slices/practiceIntegration';
import activityReducer from './slices/activity';
import analyticsReducer from './slices/analytics';
import afterLoginReducer from './slices/afterLogin';
import autoReminderReducer from './slices/autoReminder';
import superAdminReducer from './slices/superAdmin';
import callScriptReducer from './slices/callScript';
import chatReducer from './slices/chat';
import loanReducer from './slices/loan';
import financialReducer from './slices/financialTracker';
import campaignReducer from './slices/campaign';
import duplicateReducer from './slices/duplicateLeads';
import clinCheckReducer from './slices/clincheck';
import audienceManagerReducer from './slices/audienceManager';
import archiveReducer from './slices/archive';
import tablePreferencesReducer from './slices/tablePreferences';
import customTableTabsReducer from './slices/customTableTabs';
import builtInTabOverridesReducer from './slices/builtInTabOverrides';
import multiTreatmentReducer from './slices/multiTreatment';
import widgetCounterReducer from './slices/widgetCounter';
import toDoNotificationsReducer from './slices/toDoNotifications';
import schemaReducer from './slices/schema';
import emailCounterReducer from './slices/emailCounter';
import feedbackReviewReducer from './slices/feedbackReview';
import practiceChatbotReducer from './slices/practiceChatbot';
import postsReducer from './slices/postSlice';
import whatsappAccountReducer from './slices/whatsappAccount';
import whatsAppReducer from './slices/whatsapp';
import liveNotificationsReducer from './slices/liveNotificationSlice';
import whatsappMessagesReducer from './slices/whatsAppMessages';
import depositReducer from './slices/deposit';
import settingsTemplateReducer from './slices/settingsTemplate';
import loaderReducer from './slices/loader';
import callScriptV2Reducer from './slices/callScriptV2';
import mergeTagReducer from "./slices/mergeTags";
import allPracticeReports from "./slices/allPracticeReports";
import whatsappV2 from "./slices/whatsappV2";
import analyticsReports from "./slices/analyticsReports";
import patientsReducer from './slices/patients';
import reviewStatsCacheReducer from './slices/reviewStatsCache';
import practiceCallLogsReducer from './slices/practiceCallLogs';
import googleAdsReducer from './slices/googleAds';
import googleAnalyticsReducer from './slices/googleAnalytics';
import metaAdsReducer from './slices/metaAds';
import tiktokAdsReducer from './slices/tiktokAds';
import dentallyPractitionersReducer from './slices/dentallyPractitioners';
import instagramReducer from './slices/instagram';
import userPerformanceReducer from './slices/userPerformance';
import superAdminUsersReducer from './slices/superAdminUsers';
import scopedAdminUsersReducer from './slices/scopedAdminUsers';
import superAdminDomainsReducer from './slices/superAdminDomains';
import campaignBuilderReducer from './slices/campaignBuilder';
import spamLeadsReducer from './slices/spamLeads';
import createLeadDialogReducer from './slices/createLeadDialog';
import contactLeadPopupReducer from './slices/contactLeadPopup';
import tiktokIntegrationReducer, { resetTikTokScope } from './slices/tiktokIntegration';
import consentFormReducer from './slices/consentForm';
import bookingIntegrationReducer from './slices/bookingIntegration';
import voiceConnectionsReducer from './slices/voiceConnections';
import dentallyReportingReducer from './slices/dentallyReporting';
import superAdminPracticesReducer from './slices/superAdminPractices';
import consentV2Reducer from './slices/consentV2';
import formDeliveriesReducer from './slices/formDeliveries';
import permissionsReducer from './slices/permissions';
import pmsScheduleReducer from './slices/pmsSchedule';
import groupDashboardReducer from './slices/groupDashboard';
import contactRoundReducer from './slices/contactRound';
import surveyFormReducer from './slices/surveyForm';
import taskReducer from './slices/taskSlice';
import taskNotificationReducer from './slices/taskNotificationSlice';
import taskTemplateReducer from './slices/taskTemplateSlice';
import storageReducer from './slices/storage';
import formReportsReducer from './slices/formReports';
import tasksV2MigrationReducer from './slices/tasksV2Migration';
import tasksV2DryRunPreviewReducer from './slices/tasksV2DryRunPreview';
import tasksV2ExecuteResultsReducer from './slices/tasksV2ExecuteResults';
// Cockpit-coverage trio (Solutions A / B / C — see PLAN.md at
// /Freelance Projects/tasks-v2-migration-cockpit-coverage/PLAN.md).
// Each is a standalone slice intentionally separate from
// tasksV2Migration to keep the cockpit-master slice from growing
// unbounded.
import tasksV2DefaultTemplatesReducer from './slices/tasksV2DefaultTemplates';
import tasksV2ChecklistConfigReducer from './slices/tasksV2ChecklistConfig';
import tasksV2CoverageMatrixReducer from './slices/tasksV2CoverageMatrix';
// Mirror Activity Feed (Slice 1) + lineage drawer (Slice 2). Same
// "isolated slice per panel" rationale as the cockpit-coverage trio
// above — keeps the omnibus tasksV2Migration slice from churning every
// time the activity feed paginates or the drawer opens.
import tasksV2MirrorActivityReducer from './slices/tasksV2MirrorActivity';
import systemTemplatesReducer from './slices/systemTemplates';
// PR-9 — per-practice patient acknowledgement toggles surfaced in the
// super-admin Notification Management screen alongside (not replacing)
// the existing staff-side notification config tabs.
import patientNotificationPreferencesReducer from './slices/patientNotificationPreferences';
import instagramInboxReducer from './slices/instagramInbox';
import launchpadReducer from './slices/launchpad';
import unbounceIntegrationReducer from './slices/unbounceIntegration';

// Strip transient loading flags from the V1 whatsApp slice before persisting.
// A session hydrated with threadLoading=true would suppress the conversation
// list indefinitely — the flag must always start false on page load.
const whatsAppPersistTransform = createTransform(
    // inbound: state → storage (write). Drop the flag entirely.
    (inboundState) => {
        const state = { ...inboundState };
        delete state.threadLoading;
        return state;
    },
    // outbound: storage → state (rehydrate). Normalise to false regardless of
    // what was stored (guards pre-fix sessions that persisted threadLoading=true).
    (outboundState) => ({ ...outboundState, threadLoading: false }),
    { whitelist: ['whatsApp'] }
);

// Normalise transient prefetch flag, and the needsResponse cache's
// completeness contract, on rehydration.
//
// prefetchInFlight is only meaningful within a single page session — it must
// always start false on load. Guards the edge case where a crash (or a
// feature-access revocation between sessions) persists prefetchInFlight=true,
// which would cause LaunchpadReviewsCarousel to wait forever for a prefetch
// that will never complete.
//
// needsResponse cache completeness (reviews-cache completeness, Design A):
// every entry now carries an explicit `complete` flag so a reader (e.g.
// ManageBusiness.loadNeedsResponse) can tell a full list from a short
// dashboard-prefetch preview without re-deriving it. A localStorage payload
// written before this change (or a legacy bare-array entry) has no such
// flag — on rehydrate, normalise every entry to the object shape with
// `complete` explicitly false. That is the safe direction: it costs one
// extra pagination request the first time the reviews page is opened, not a
// stale short list rendered as if it were the full one.
const reviewStatsCachePersistTransform = createTransform(
    // inbound: state → storage (write). Pass through unchanged.
    (inboundState) => inboundState,
    // outbound: storage → state (rehydrate). Clear the in-flight flag and
    // normalise the needsResponse map's completeness flag.
    (outboundState) => {
        const needsResponse = outboundState?.needsResponse;
        const normalizedNeedsResponse = (needsResponse && typeof needsResponse === 'object')
            ? Object.fromEntries(
                Object.entries(needsResponse).map(([key, entry]) => {
                    if (Array.isArray(entry)) {
                        // Legacy bare-array shape → object shape, incomplete.
                        return [key, { data: entry, count: entry.length, complete: false }];
                    }
                    if (entry && typeof entry === 'object') {
                        // Object shape, but a missing/non-true flag reads as incomplete.
                        return [key, { ...entry, complete: entry.complete === true }];
                    }
                    return [key, entry];
                })
            )
            : needsResponse;

        return {
            ...outboundState,
            prefetchInFlight: false,
            needsResponse: normalizedNeedsResponse,
        };
    },
    { whitelist: ['reviewStatsCache'] }
);

const rootPersistConfig = {
    key: 'root',
    storage,
    keyPrefix: 'redux-',
    whitelist: ['whatsApp', 'mergeTags', 'reviewStatsCache', 'permissions'],
    transforms: [whatsAppPersistTransform, reviewStatsCachePersistTransform],
};

const appReducer = combineReducers({
    attendanceReview,
    whatsappV2,
    allPracticeReports,
    analyticsReports,
    mail: mailReducer,
    calendar: calendarReducer,
    kanban: kanbanReducer,
    user: userReducer,
    lead: leadReducer,
    forgotpassword: forgotpasswordReducer,
    practice: practiceReducer,
    singlePractice: singlePracticeReducer,
    practiceTreatment: practiceTreatmentReducer,
    practiceMessage: practiceMessageReducer,
    practiceTreatmentList: practiceTreatmentListReducer,
    practiceUser: practiceUserReducer,
    tagsCreate: tagsCreateReducer,
    practiceIntegration: practiceIntegrationReducer,
    activity: activityReducer,
    analytics: analyticsReducer,
    afterLogin: afterLoginReducer,
    autoReminder: autoReminderReducer,
    superAdmin: superAdminReducer,
    callScript: callScriptReducer,
    chat: chatReducer,
    loan: loanReducer,
    financialTracker: financialReducer,
    campaign: campaignReducer,
    duplicate: duplicateReducer,
    clinCheck: clinCheckReducer,
    audienceManager: audienceManagerReducer,
    archive: archiveReducer,
    tablePreferences: tablePreferencesReducer,
    customTableTabs: customTableTabsReducer,
    builtInTabOverrides: builtInTabOverridesReducer,
    multiTreatment: multiTreatmentReducer,
    widgetCounter: widgetCounterReducer,
    toDoNotifications: toDoNotificationsReducer,
    schema: schemaReducer,
    emailCounter: emailCounterReducer,
    feedbackReview: feedbackReviewReducer,
    practiceChatbot: practiceChatbotReducer,
    posts: postsReducer,
    whatsappAccount: whatsappAccountReducer,
    whatsApp: whatsAppReducer,
    liveNotifications: liveNotificationsReducer,
    whatsappMessages: whatsappMessagesReducer,
    deposit: depositReducer,
    settingsTemplate: settingsTemplateReducer,
    loaderReducer,
    callScriptV2: callScriptV2Reducer,
    mergeTags: mergeTagReducer,
    patients: patientsReducer,
    reviewStatsCache: reviewStatsCacheReducer,
    practiceCallLogs: practiceCallLogsReducer,
    googleAds: googleAdsReducer,
    googleAnalytics: googleAnalyticsReducer,
    metaAds: metaAdsReducer,
    tiktokAds: tiktokAdsReducer,
    dentallyPractitioners: dentallyPractitionersReducer,
    instagram: instagramReducer,
    userPerformance: userPerformanceReducer,
    superAdminUsers: superAdminUsersReducer,
    scopedAdminUsers: scopedAdminUsersReducer,
    superAdminDomains: superAdminDomainsReducer,
    campaignBuilder: campaignBuilderReducer,
    spamLeads: spamLeadsReducer,
    createLeadDialog: createLeadDialogReducer,
    contactLeadPopup: contactLeadPopupReducer,
    tiktokIntegration: tiktokIntegrationReducer,
    consentForm: consentFormReducer,
    bookingIntegration: bookingIntegrationReducer,
    voiceConnections: voiceConnectionsReducer,
    dentallyReporting: dentallyReportingReducer,
    superAdminPractices: superAdminPracticesReducer,
    consentV2: consentV2Reducer,
    formDeliveries: formDeliveriesReducer,
    permissions: permissionsReducer,
    pmsSchedule: pmsScheduleReducer,
    groupDashboard: groupDashboardReducer,
    contactRound: contactRoundReducer,
    surveyForm: surveyFormReducer,
    task: taskReducer,
    taskNotification: taskNotificationReducer,
    taskTemplate: taskTemplateReducer,
    storage: storageReducer,
    formReports: formReportsReducer,
    tasksV2Migration: tasksV2MigrationReducer,
    tasksV2DryRunPreview: tasksV2DryRunPreviewReducer,
    tasksV2ExecuteResults: tasksV2ExecuteResultsReducer,
    tasksV2DefaultTemplates: tasksV2DefaultTemplatesReducer,
    tasksV2ChecklistConfig: tasksV2ChecklistConfigReducer,
    tasksV2CoverageMatrix: tasksV2CoverageMatrixReducer,
    tasksV2MirrorActivity: tasksV2MirrorActivityReducer,
    systemTemplates: systemTemplatesReducer,
    patientNotificationPreferences: patientNotificationPreferencesReducer,
    instagramInbox: instagramInboxReducer,
    launchpad: launchpadReducer,
    unbounceIntegration: unbounceIntegrationReducer,
});

/**
 * Root reducer with practice-switch reset semantics.
 *
 * Handing combineReducers a state object that only contains the preserved keys
 * makes it initialise every other slice from its own initialState — the
 * standard Redux "reset the store" shape, expressed as an allowlist so the
 * default for anything new is "practice-scoped, therefore wiped".
 *
 * redux-persist strips `_persist` before calling this, so the partial object
 * never trips combineReducers' unexpected-key warning.
 */
const rootReducer = (state, action) => {
    const switching = action.type === PRACTICE_SWITCH_RESET;
    const next = appReducer(switching ? projectPreservedState(state, PRACTICE_AGNOSTIC_SLICES) : state, action);
    if (state && (switching || state.practiceTreatmentList.practiceId !== next.practiceTreatmentList.practiceId)) {
        next.tiktokIntegration = tiktokIntegrationReducer(state.tiktokIntegration, resetTikTokScope({
            sessionKey: state.tiktokIntegration.sessionKey,
            practiceId: next.practiceTreatmentList.practiceId,
        }));
    }
    return next;
};

export { rootPersistConfig, rootReducer };
